import { forwardRef } from 'react'
import type { CSSProperties, InputHTMLAttributes } from 'react'
import { ItIcon } from '../primitives/it-icon'
import type { ItIconName } from '../primitives/it-icon'
import { cn } from '../primitives/util'
import { Field } from './field'

/**
 * Labeled text input with optional leading icon, hint, and error wiring
 * (aria-describedby + role="alert", per doctrine §3.4). shadcn/Tailwind over
 * the token bridge; focus/invalid/disabled are data-attribute driven.
 */

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  hint?: string
  error?: string
  icon?: ItIconName
  /** Wrapper style override (e.g. width). */
  wrapperStyle?: CSSProperties
}

const inputBox =
  'flex h-[var(--control-md)] items-center gap-2 rounded-[var(--r-input)] border border-[var(--border-strong)] bg-card px-3 text-foreground transition-[border-color,box-shadow,background-color] hover:border-[var(--text-faint)] focus-within:border-[var(--brand-500)] focus-within:shadow-[var(--focus-ring)] data-[invalid]:border-destructive data-[disabled]:cursor-not-allowed data-[disabled]:bg-secondary data-[disabled]:text-[var(--text-faint)]'

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, icon, wrapperStyle, disabled, className, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} style={wrapperStyle}>
      {(control) => (
        <div
          className={cn(inputBox, className)}
          data-invalid={error ? '' : undefined}
          data-disabled={disabled ? '' : undefined}
        >
          {icon && (
            <span className="text-[var(--text-faint)]" aria-hidden="true">
              <ItIcon name={icon} size={16} color="currentColor" />
            </span>
          )}
          <input
            ref={ref}
            disabled={disabled}
            className="min-w-0 flex-1 border-0 bg-transparent text-[length:var(--fs-13)] text-foreground outline-none placeholder:text-[var(--text-faint)]"
            {...control}
            {...rest}
          />
        </div>
      )}
    </Field>
  )
})
